import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "./api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => { try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed", e); } setUser(null); };

  return <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>{children}</AuthContext.Provider>;
}

// Handles #session_id=... after Google OAuth
export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [error, setError] = useState("");

  useEffect(() => {
    const run = async () => {
      const hash = location.hash || window.location.hash;
      const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (!sessionId) { navigate("/admin/login"); return; }
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        setUser(data);
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Sign-in failed. This account may not be authorized.");
      }
    };
    run();
  }, []); // eslint-disable-line

  return (
    <div className="min-h-screen grid place-items-center bg-[#0A0A0A] text-white px-6">
      {error ? (
        <div className="text-center max-w-md">
          <p className="text-[#E63946] font-display font-bold text-2xl mb-3">Access Denied</p>
          <p className="text-white/60 mb-6">{error}</p>
          <button onClick={() => navigate("/admin/login")} className="rounded-full bg-[#E63946] px-6 py-3">Back to Login</button>
        </div>
      ) : (
        <p className="text-white/60 animate-pulse">Signing you in…</p>
      )}
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (!loading && !user) navigate("/admin/login", { replace: true }); }, [loading, user, navigate]);
  if (loading) return <div className="min-h-screen grid place-items-center bg-[#0A0A0A] text-white"><p className="animate-pulse text-white/60">Loading…</p></div>;
  if (!user) return null;
  return children;
}
